interface PinStyle {
  color: string;
  // A Material Symbols ligature name (rendered via .material-symbols-outlined,
  // self-hosted font is global from the layout), or 'svg:horse' / 'svg:carriage'
  // for the two categories with no font glyph. Keep icon names in sync with
  // app/helpers/categories_helper.rb (CATEGORY_ICONS) so map pins and listing
  // pills match. Keyed by category slug.
  icon: string;
}

const CATEGORY_STYLE: Record<string, PinStyle> = {
  smestaj:                        { color: '#8B5E3C', icon: 'cottage' },
  kvad:                           { color: '#E85D3B', icon: 'sports_motorsports' },
  biciklizam:                     { color: '#5B54C8', icon: 'pedal_bike' },
  jahanje:                        { color: '#9B5A2B', icon: 'svg:horse' },
  'voznja-kocijom':               { color: '#7E5A3C', icon: 'svg:carriage' },
  plovidba:                       { color: '#2A7FBE', icon: 'sailing' },
  kajak:                          { color: '#1E9E7A', icon: 'kayaking' },
  jezero:                         { color: '#2E8B9E', icon: 'waves' },
  plaza:                          { color: '#E0A93B', icon: 'beach_access' },
  vinarija:                       { color: '#7C2F4A', icon: 'wine_bar' },
  restoran:                       { color: '#C0453A', icon: 'restaurant' },
  hiking:                         { color: '#3E7E3E', icon: 'hiking' },
  tvrdjava:                       { color: '#6E6E6E', icon: 'castle' },
  manastir:                       { color: '#B08B3A', icon: 'church' },
  'kulturni-istorijski-spomenik': { color: '#4A4A4A', icon: 'account_balance' },
  vodopad:                        { color: '#2FA3B8', icon: 'water_drop' },
  vidikovac:                      { color: '#5A8C4A', icon: 'landscape' },
  'wellness-spa':                 { color: '#4FA58C', icon: 'spa' },
  'etno-selo':                    { color: '#9A7B3A', icon: 'holiday_village' },
  glamping:                       { color: '#8E44AD', icon: 'camping' },
  'zabavni-park':                 { color: '#A8478E', icon: 'attractions' },
};

const DEFAULT_STYLE: PinStyle = { color: '#333', icon: 'location_on' };

// Inline SVGs for the categories with no font glyph. Mirror of the horse/carriage
// in app/helpers/categories_helper.rb (server renders the pills, we render the
// pins). Stroked with currentColor so the pin's white text color drives them.
const CUSTOM_SVG: Record<string, string> = {
  'svg:horse':
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10l-.85 8.507a1.357 1.357 0 0 0 1.35 1.493h.146a2 2 0 0 0 1.857 -1.257l.994 -2.486a2 2 0 0 1 1.857 -1.257h1.292a2 2 0 0 1 1.857 1.257l.994 2.486a2 2 0 0 0 1.857 1.257h.146a1.37 1.37 0 0 0 1.364 -1.494l-.864 -9.506h-8c0 -3 -3 -5 -6 -5l-3 6l2 2l3 -2"/><path d="M22 14v-2a3 3 0 0 0 -3 -3"/></svg>',
  'svg:carriage':
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="18.5" r="2.3"/><circle cx="15" cy="18.5" r="2.3"/><path d="M6 16 V12 Q6 9 9 9 H13 Q16 9 16 12 V16 Z"/><line x1="11" y1="9" x2="11" y2="16"/><path d="M16 13 L22 11"/></svg>',
};

// Builds the white icon element that sits inside a colored pin: a Material
// Symbols glyph, or an inline SVG for horse/carriage. Color is inherited
// (currentColor) from the pin's `color:white`.
function buildIcon(icon: string): HTMLElement {
  const el = document.createElement('span');
  el.style.cssText = 'display:flex;align-items:center;justify-content:center;line-height:1;';
  if (icon.startsWith('svg:')) {
    el.innerHTML = CUSTOM_SVG[icon];
  } else {
    el.className = 'material-symbols-outlined';
    el.style.fontSize = '22px';
    el.textContent = icon;
  }
  return el;
}

export function categoryColor(categorySlug: string | null): string {
  return ((categorySlug && CATEGORY_STYLE[categorySlug]) || DEFAULT_STYLE).color;
}

export function buildCategoryPin(categorySlug: string | null, opts?: { inPlan?: boolean }): HTMLElement {
  const style = (categorySlug && CATEGORY_STYLE[categorySlug]) || DEFAULT_STYLE;

  const wrapper = document.createElement('div');
  wrapper.className = 'djir-pin';
  wrapper.style.cssText =
    'width:44px;height:44px;display:flex;align-items:center;justify-content:center;';

  const inner = document.createElement('div');
  inner.style.cssText = [
    'width:36px',
    'height:36px',
    'border-radius:50%',
    `background:${style.color}`,
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'color:white',
    'box-shadow:0 2px 6px rgba(0,0,0,0.3), 0 0 0 3px white inset',
  ].join(';');
  inner.appendChild(buildIcon(style.icon));

  wrapper.appendChild(inner);

  if (opts?.inPlan) {
    wrapper.appendChild(buildCheckBadge());
    wrapper.style.position = 'relative';
  }

  return wrapper;
}

// Anchor pin for a group of co-located listings (same GPS spot). Shows the count
// so the user knows the pin fans out into several listings when clicked.
const GROUP_PIN_COLOR = '#374151'; // slate gray, neutral vs. category colors

export function buildGroupPin(count: number, opts?: { inPlan?: boolean }): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'djir-pin djir-pin-group';
  wrapper.style.cssText =
    'width:44px;height:44px;position:relative;display:flex;align-items:center;justify-content:center;';

  const inner = document.createElement('div');
  inner.style.cssText = [
    'width:36px',
    'height:36px',
    'border-radius:50%',
    `background:${GROUP_PIN_COLOR}`,
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'color:white',
    'font-size:16px',
    'font-weight:700',
    'line-height:1',
    'box-shadow:0 2px 6px rgba(0,0,0,0.3), 0 0 0 3px white inset',
  ].join(';');
  inner.textContent = String(count);

  wrapper.appendChild(inner);

  if (opts?.inPlan) {
    wrapper.appendChild(buildCheckBadge());
  }

  return wrapper;
}

// Proximity cluster bubble: several nearby (but not co-located) pins collapsed
// into one at low zoom. Shows the true total number of underlying listings.
// Visually distinct from buildGroupPin's slate "N" so a spread-out cluster reads
// apart from a same-GPS-spot group. Brand green, slightly larger.
const CLUSTER_PIN_COLOR = '#2e7d32'; // djir route/brand green

export function buildClusterPin(count: number): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'djir-pin djir-pin-cluster';
  wrapper.style.cssText =
    'width:48px;height:48px;display:flex;align-items:center;justify-content:center;';

  const inner = document.createElement('div');
  inner.style.cssText = [
    'width:42px',
    'height:42px',
    'border-radius:50%',
    `background:${CLUSTER_PIN_COLOR}`,
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'color:white',
    'font-size:16px',
    'font-weight:700',
    'line-height:1',
    'box-shadow:0 2px 6px rgba(0,0,0,0.3), 0 0 0 3px white inset',
  ].join(';');
  inner.textContent = String(count);

  wrapper.appendChild(inner);
  return wrapper;
}

// Distinct marker for the plan's root starting point (a city/village, not a
// listing). Blue circle + home glyph so it reads apart from category pins.
const START_STYLE: PinStyle = { color: '#1d4ed8', icon: 'home' }; // blue, unused by any category

export function buildStartPin(): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'djir-pin djir-pin-start';
  wrapper.style.cssText =
    'width:44px;height:44px;display:flex;align-items:center;justify-content:center;';

  const inner = document.createElement('div');
  inner.style.cssText = [
    'width:38px',
    'height:38px',
    'border-radius:50%',
    `background:${START_STYLE.color}`,
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'color:white',
    'box-shadow:0 2px 6px rgba(0,0,0,0.35), 0 0 0 3px white inset',
  ].join(';');
  inner.appendChild(buildIcon(START_STYLE.icon));

  wrapper.appendChild(inner);
  return wrapper;
}

function buildCheckBadge(): HTMLElement {
  const badge = document.createElement('span');
  badge.className = 'djir-pin-check';
  badge.textContent = '✓';
  badge.style.cssText = [
    'position:absolute',
    'top:2px',
    'right:2px',
    'width:16px',
    'height:16px',
    'border-radius:50%',
    'background:#fff',
    'color:#2e7d32',
    'font-size:11px',
    'font-weight:700',
    'line-height:16px',
    'text-align:center',
    'box-shadow:0 1px 3px rgba(0,0,0,0.3)',
  ].join(';');
  return badge;
}
